import * as Yup from "yup";

/**
 * OPD appointment invoice type (REGISTRATION vs CONSULTATION) and amount rules.
 * Keeps UI aligned with patientPeriodSummary (REGISTRATION = new, CONSULTATION = returning).
 */

export const APPOINTMENT_INVOICE_TYPES = {
  REGISTRATION: "REGISTRATION",
  CONSULTATION: "CONSULTATION",
};

/**
 * Invoice type options for the appointment billing step.
 * - New patient → REGISTRATION only
 * - Existing patient with no OPD history (no REG/CONSULT invoice, no appointment) → REGISTRATION only
 * - Existing old patient (any REG/CONSULT invoice or any appointment) → CONSULTATION only
 *   (after configured OPD waiver days since last paid visit, amount must be > 0 — see amount rules)
 */
export function normalizeOpdInvoiceType(invoice) {
  return String(
    invoice?.invoiceType || invoice?.invoice_type || invoice?.indicator || ""
  )
    .trim()
    .toUpperCase();
}

export function hasOpdRegistrationOrConsultationInvoice(invoices = []) {
  return (Array.isArray(invoices) ? invoices : []).some((invoice) => {
    const type = normalizeOpdInvoiceType(invoice);
    return (
      type === APPOINTMENT_INVOICE_TYPES.REGISTRATION ||
      type === APPOINTMENT_INVOICE_TYPES.CONSULTATION
    );
  });
}

export function hasAnyPatientAppointments(appointments = []) {
  return Array.isArray(appointments) && appointments.length > 0;
}

/**
 * True when an existing patient has never had an OPD appointment or
 * REGISTRATION/CONSULTATION invoice (other invoice types are ignored).
 */
export function isFirstOpdBillingVisit({
  invoices = [],
  appointments = [],
} = {}) {
  if (hasAnyPatientAppointments(appointments)) return false;
  if (hasOpdRegistrationOrConsultationInvoice(invoices)) return false;
  return true;
}

export function getAppointmentInvoiceTypeOptions({
  hasExistingPatient = false,
  isFirstOpdBillingVisit: isFirstOpdVisit = false,
} = {}) {
  if (!hasExistingPatient) return [APPOINTMENT_INVOICE_TYPES.REGISTRATION];
  if (isFirstOpdVisit) return [APPOINTMENT_INVOICE_TYPES.REGISTRATION];
  return [APPOINTMENT_INVOICE_TYPES.CONSULTATION];
}

/**
 * After the configured OPD billing gap, CONSULTATION must be billed with amount > 0.
 * Within the gap (when waiver enabled), amount may be ₹0 for follow-up visits.
 */
export function requiresPositiveConsultationAmount({
  hasExistingPatient = false,
  isFirstOpdBillingVisit: isFirstOpdVisit = false,
  allowRegistrationAfterGap = false,
  indicator = "",
} = {}) {
  if (String(indicator || "").trim().toUpperCase() !== APPOINTMENT_INVOICE_TYPES.CONSULTATION) {
    return false;
  }
  return (
    hasExistingPatient &&
    !isFirstOpdVisit &&
    Boolean(allowRegistrationAfterGap)
  );
}

/** Returns an error message when amount is invalid, otherwise null. */
export function validateAppointmentInvoiceAmount({
  amount,
  indicator,
  hasExistingPatient = false,
  isFirstOpdBillingVisit: isFirstOpdVisit = false,
  allowRegistrationAfterGap = false,
} = {}) {
  const subtotal = Number(amount) || 0;
  const type = String(indicator || "").trim().toUpperCase();

  if (type === APPOINTMENT_INVOICE_TYPES.CONSULTATION) {
    if (
      requiresPositiveConsultationAmount({
        hasExistingPatient,
        isFirstOpdBillingVisit: isFirstOpdVisit,
        allowRegistrationAfterGap,
        indicator: type,
      })
    ) {
      return subtotal > 0 ? null : "Amount must be greater than 0";
    }
    return subtotal >= 0 ? null : "Amount must be 0 or greater";
  }

  if (type === APPOINTMENT_INVOICE_TYPES.REGISTRATION) {
    return subtotal > 0 ? null : "Amount must be greater than 0";
  }

  return subtotal > 0 ? null : "Amount must be greater than 0";
}

export function isAppointmentInvoiceSubmitDisabled({
  amount = 0,
  indicator = "",
  paymentMode = null,
  hasExistingPatient = false,
  isFirstOpdBillingVisit: isFirstOpdVisit = false,
  allowRegistrationAfterGap = false,
} = {}) {
  if (Number(amount) < 0) return true;

  const payment = paymentMode || {};
  if (!payment.mode) return true;
  if (payment.mode === "credit" && !payment.organizationId) return true;

  return Boolean(
    validateAppointmentInvoiceAmount({
      amount,
      indicator,
      hasExistingPatient,
      isFirstOpdBillingVisit: isFirstOpdVisit,
      allowRegistrationAfterGap,
    })
  );
}

export function createAppointmentAmountValidationSchema({
  hasExistingPatient = false,
  isFirstOpdBillingVisit: isFirstOpdVisit = false,
  allowRegistrationAfterGap = false,
} = {}) {
  return Yup.number()
    .nullable()
    .when(["appointment", "indicator"], {
      is: (appointment, indicator) =>
        Boolean(appointment) &&
        requiresPositiveConsultationAmount({
          hasExistingPatient,
          isFirstOpdBillingVisit: isFirstOpdVisit,
          allowRegistrationAfterGap,
          indicator,
        }),
      then: (schema) =>
        schema
          .required("Invoice amount is required")
          .min(0.01, "Amount must be greater than 0"),
      otherwise: (schema) =>
        schema.when(["appointment", "indicator"], {
          is: (appointment, indicator) =>
            Boolean(appointment) &&
            String(indicator || "").trim().toUpperCase() ===
              APPOINTMENT_INVOICE_TYPES.CONSULTATION,
          then: (inner) =>
            inner
              .required("Invoice amount is required")
              .min(0, "Amount must be 0 or greater"),
          otherwise: (inner) =>
            inner.when("appointment", {
              is: true,
              then: (requiredSchema) =>
                requiredSchema
                  .required("Invoice amount is required")
                  .min(0.01, "Amount must be greater than 0"),
              otherwise: (optionalSchema) => optionalSchema.nullable(),
            }),
        }),
    });
}
